/**
 * Guia — capítulos "Primeiros passos" e "Conexões".
 *
 * As conexões REAPROVEITAM `features/guias/conexoes.ts` (o mesmo texto que aparece ao lado do
 * formulário nas telas de Monitoradores, Redirecionadores e Meta Ads). Aqui só entram as
 * imagens e, quando aquele texto envelheceu, a correção declarada em `CORRECOES` — com o
 * motivo, para alguém poder apagá-la quando `conexoes.ts` for atualizado.
 */
import { Plug, Rocket } from 'lucide-react';

import {
  GUIA_META_ADS,
  GUIA_REDIRECIONADOR,
  GUIA_WHATSAPP,
  type GuiaDeConexao,
} from '../guias/conexoes';
import { img } from './imagens';
import type { CapituloDoGuia, ImagemDoGuia, PassoDoGuia, SecaoDoGuia } from './tipos';

export const PARAMETROS_DE_URL =
  'utm_campaign={{campaign.id}}&utm_term={{adset.id}}&utm_content={{ad.id}}';

/* ========================================================================== */
/*  Primeiros passos                                                           */
/* ========================================================================== */

export const PRIMEIROS_PASSOS: CapituloDoGuia = {
  id: 'primeiros-passos',
  titulo: 'Primeiros passos',
  resumo:
    'O tutorial de quem acabou de chegar: o que o MGDLead mede e o caminho do zero até o primeiro custo por entrada na tela.',
  icone: Rocket,
  capa: img('dashboard', 'Dashboard da Loja Exemplo aberto em Hoje, com o cartão violeta do dia', [
    'Período: Hoje, 7, 30, 90 dias ou datas',
    'O resumo do dia, em uma frase',
    'O que pede a sua atenção agora',
    'Quem entrou hoje e ficou',
  ]),
  secoes: [
    {
      id: 'o-que-e',
      titulo: 'O que o MGDLead faz por você',
      resumo: 'Cinco minutos de leitura que poupam horas de "por que esse número não bate?".',
      passos: [
        {
          id: 'caminho',
          titulo: 'Ele acompanha o caminho inteiro: do anúncio ao grupo',
          texto:
            'Quem vende por grupo de WhatsApp paga um anúncio, manda a pessoa para uma página, a pessoa toca em "Entrar no grupo" e — com sorte — fica. O Gerenciador de Anúncios para no clique; o WhatsApp não diz de onde ninguém veio. O MGDLead liga as duas pontas.\n\nCom isso você passa a ver o número que importa de verdade: quanto custou cada pessoa que entrou no grupo (e cada uma que ficou), por campanha, por anúncio e por grupo.',
          itens: [
            'Anúncio na Meta — o gasto, as impressões e as visitas na sua página chegam sozinhos depois que você conecta a conta.',
            'Sua página de ofertas — o botão "Entrar no grupo" aponta para o link do MGDLead (o redirecionador).',
            'Redirecionador — registra o clique, de qual anúncio ele veio, e escolhe o grupo com vaga.',
            'Grupo de WhatsApp — um número seu (o monitorador) fica dentro do grupo e avisa quem entra e quem sai.',
            'Permanência — quanto tempo cada pessoa fica, e quanto do tráfego pago continua no grupo depois de 1, 7, 30 dias.',
          ],
        },
        {
          id: 'vocabulario',
          titulo: 'As quatro palavras que aparecem em todo lugar',
          texto:
            'O painel inteiro é montado com quatro peças. Vale guardar o nome de cada uma, porque as telas e este guia conversam com elas o tempo todo.',
          blocos: [
            {
              rotulo: 'Monitorador',
              texto:
                'Um número de WhatsApp conectado ao MGDLead. Ele fica dentro dos grupos e é por ele que o painel enxerga entradas e saídas. Um número acompanha vários grupos.',
            },
            {
              rotulo: 'Grupo',
              texto:
                'Um grupo do WhatsApp que você escolheu acompanhar. Cabe até 1.024 pessoas; o painel para de mandar gente perto de 980 (o limite de segurança).',
            },
            {
              rotulo: 'Campanha (bloco)',
              texto:
                'Um conjunto de grupos que recebe gente de um mesmo anúncio — por exemplo "Ofertas #01 a #08". É nela que você liga os grupos e as campanhas da Meta.',
            },
            {
              rotulo: 'Redirecionador',
              texto:
                'O link curto que vai no botão da sua página. Ele escolhe o grupo de cada pessoa e guarda de qual anúncio ela veio.',
            },
          ],
        },
        {
          id: 'zero-nao-e-vazio',
          titulo: '"—" não é zero',
          texto:
            'Em todo o painel, um travessão quer dizer "ainda não medimos" — falta uma conexão, um vínculo ou o tempo passar. Zero é zero: medimos e não aconteceu. Parece detalhe, mas é o que evita tomar decisão de verba em cima de um número que não existe.',
          notas: [
            {
              tom: 'dica',
              titulo: 'Toque no (?) ao lado de qualquer número',
              texto:
                'Todo indicador tem uma explicação curta de como ele é medido. E o capítulo "Tudo que o MGDLead mede" explica cada um com calma.',
            },
          ],
        },
      ],
    },
    {
      id: 'do-zero',
      titulo: 'Do zero ao primeiro número',
      resumo:
        'A ordem que funciona. Cada passo leva poucos minutos; o capítulo "Conexões" detalha os três que têm pegadinha.',
      passos: [
        {
          id: 'conta',
          titulo: 'Crie a conta e o workspace — com o fuso certo',
          onde: 'app.mgdleads.tech/cadastro',
          texto:
            'Nome, e-mail e uma senha com pelo menos 12 caracteres; confirme pelo link que chega no e-mail. Depois dê o nome da sua operação ao workspace e confira o fuso horário: é ele que define o que é "hoje" em todos os números do painel.',
          notas: [
            {
              tom: 'atencao',
              titulo: 'O fuso muda o "hoje"',
              texto:
                'Uma entrada às 23h30 de São Paulo já é "amanhã" em Lisboa. Escolha o fuso de onde a operação é acompanhada. Dá para trocar depois em Configurações › Workspace.',
            },
          ],
        },
        {
          id: 'monitorador',
          titulo: 'Conecte um número de WhatsApp (o monitorador)',
          onde: 'WhatsApp › Monitoradores',
          texto:
            'Toque em "Adicionar monitorador", dê um nome que identifique o aparelho ("Chip Ofertas 1") e leia o QR code pelo WhatsApp do celular, em Aparelhos conectados. Espere a situação virar "Conectado".',
          imagem: img('monitoradores', 'Tela de Monitoradores com dois números conectados', [
            'Adicionar monitorador',
            'Situação do número (Conectado)',
            'Mais ações: detalhes, renomear, desconectar, arquivar',
          ]),
          notas: [
            {
              tom: 'dica',
              titulo: 'Atalho: use um número que já está em todos os grupos',
              texto:
                'Se o seu número de administrador já participa de todos os grupos e fica sempre ligado, conecte ele. Assim você não precisa colocar número novo em grupo nenhum.',
            },
          ],
        },
        {
          id: 'grupos',
          titulo: 'Escolha os grupos que quer acompanhar',
          onde: 'WhatsApp › Grupos',
          texto:
            'O número monitorador precisa estar dentro do grupo — isso é feito no WhatsApp, à mão (ele não precisa ser administrador). Depois, em Grupos › "Escolher grupos", busque pelo nome, marque e toque em "Monitorar N grupos".',
          notas: [
            {
              tom: 'sozinho',
              titulo: 'A primeira varredura roda sozinha',
              texto:
                'Assim que os grupos entram, o MGDLead descobre quem já está lá dentro — um grupo de cada vez, para não sobrecarregar o número. Quem já estava não conta como entrada de hoje.',
            },
          ],
        },
        {
          id: 'campanha',
          titulo: 'Crie a campanha e vincule os grupos do bloco',
          onde: 'Aquisição › Campanhas',
          texto:
            '"Nova campanha", dê um nome (o código curto é gerado sozinho) e, na linha dela, toque em "Grupos" para vincular os grupos que vão receber gente desse anúncio. Eles entram numa fila na ordem do nome: #01, #02, #03…',
        },
        {
          id: 'link',
          titulo: 'Crie o link e instale na sua página',
          onde: 'Aquisição › Redirecionadores',
          texto:
            '"Novo redirecionador", escolha a campanha e como repartir as pessoas entre os grupos. Na aba Instalação está o trecho pronto: cole o trecho INTEIRO no botão "Entrar no grupo" da sua página.',
        },
        {
          id: 'parametros',
          titulo: 'Ponha os parâmetros de URL no anúncio',
          onde: 'Gerenciador de Anúncios da Meta › Anúncio › Parâmetros de URL',
          texto:
            'É o que faz cada clique chegar com a campanha, o conjunto e o anúncio de origem. Sem isso o clique aparece como "sem origem" e o custo por entrada não fecha.',
          codigo: PARAMETROS_DE_URL,
        },
        {
          id: 'meta',
          titulo: 'Conecte a conta de anúncios e ligue a campanha da Meta ao bloco',
          onde: 'Aquisição › Meta Ads  ·  Campanhas › ⋯ › Meta Ads',
          texto:
            'Conecte a conta com o token do Usuário do Sistema (passo a passo no capítulo Conexões). Depois, em Campanhas, abra o menu ⋯ do bloco › Meta Ads, marque as campanhas da Meta que mandam gente para ele e toque em "Vincular". É aqui que o gasto encontra as entradas.',
        },
        {
          id: 'teste',
          titulo: 'Clique você mesmo e confira',
          texto:
            'Abra a sua página, toque no botão e veja se cai num grupo. Em Redirecionadores › Instalação › Cliques o seu clique aparece com a origem. Um minuto de teste antes de ligar a verba vale mais que qualquer relatório depois.',
          imagem: img('celular-dashboard', 'O Dashboard no celular, com o resumo do dia', [], {
            formato: 'celular',
            legenda:
              'No celular, o painel é o mesmo: a barra de baixo leva às telas que se olha em pé.',
          }),
        },
      ],
    },
    {
      id: 'checklist',
      titulo: 'Checklist: está tudo ligado?',
      passos: [
        {
          id: 'lista',
          titulo: 'Confira um por um',
          texto:
            'Se algum número aparece como "—" onde você esperava valor, quase sempre é um destes itens faltando.',
          itens: [
            'Conta criada, e-mail confirmado e workspace com o fuso certo',
            'Monitorador "Conectado", num número sempre ligado e dentro dos grupos',
            'Grupos escolhidos (a primeira varredura roda sozinha)',
            'Campanha criada, com os grupos do bloco vinculados',
            'Redirecionador criado e o trecho INTEIRO no botão da página',
            `Anúncio com os parâmetros de URL (${PARAMETROS_DE_URL})`,
            'Clique de teste feito e origem conferida',
            'Meta Ads conectado (token testado) e primeira leitura concluída',
            'Campanhas da Meta vinculadas ao bloco',
            'Custo por entrada aparecendo em Campanhas e na Gestão',
          ],
        },
      ],
    },
  ],
};

/* ========================================================================== */
/*  Conexões                                                                   */
/* ========================================================================== */

/**
 * Onde `conexoes.ts` ficou para trás em relação ao produto (conferido no código em 11/10):
 *  - a Meta passou a ser lida a cada 10 minutos (`contracts.ts`, `*\/10`), e existem
 *    "Sincronizar agora" (Meta Ads) e "Ler a Meta agora" (Dashboard);
 *  - "Ajustar" orçamento (só o Proprietário) precisa de `ads_management`.
 * Apague a entrada quando o texto de origem for corrigido.
 */
const CORRECOES: Record<string, { texto?: string; atencao?: string; causa?: string }> = {
  'Dê a ele acesso à conta de anúncios': {
    texto:
      'Ainda no Usuário do Sistema: Adicionar ativos › Contas de anúncios › marque a sua conta › permissão "Ver desempenho". Para só LER os números, isso basta. Se o Proprietário for usar o "Ajustar" orçamento pelo MGDLead, dê também "Gerenciar campanhas".',
  },
  'Gere o token marcando SÓ ads_read': {
    texto:
      'No mesmo Usuário do Sistema: Gerar token › escolha o app › expiração "Nunca expira" › e nas permissões marque ads_read. É o que o MGDLead usa para ler gasto, impressões e visitas.',
    atencao:
      'ads_management só é necessária se você quiser ajustar o orçamento diário pelo MGDLead (botão "Ajustar", só do Proprietário). Ela dá poder de mexer no seu dinheiro: se não vai usar, não marque. Dá para trocar o token depois, em Meta Ads › Trocar token, sem perder histórico.',
  },
  'Conectei e os números não aparecem.': {
    causa:
      'A leitura acontece a cada 10 minutos, e a primeira traz os últimos 30 dias — pode levar alguns minutos. Se o cartão mostrar erro, use "Colar um token novo".',
  },
  'Conectou certo e os números só aparecem depois.': {
    causa:
      'É o esperado: a Meta é lida a cada 10 minutos, e ela mesma atualiza o gasto com 15–30 minutos de atraso. Precisa já? "Sincronizar agora" no cartão da conta (no máximo uma vez a cada 5 minutos, para não gastar a cota da SUA conta na Meta).',
  },
};

function secoesDaConexao(
  guia: GuiaDeConexao,
  imagens: Record<number, ImagemDoGuia>,
  extra: readonly PassoDoGuia[] = [],
): SecaoDoGuia[] {
  const passos: PassoDoGuia[] = guia.passos.map((p, i) => {
    const c = CORRECOES[p.titulo];
    const atencao = c?.atencao ?? p.atencao;
    return {
      id: `${guia.id}-${i + 1}`,
      titulo: p.titulo,
      texto: c?.texto ?? p.texto,
      ...(p.exemplo ? { codigo: p.exemplo } : {}),
      ...(imagens[i] ? { imagem: imagens[i] } : {}),
      ...(atencao
        ? { notas: [{ tom: 'atencao' as const, titulo: 'O detalhe que trava', texto: atencao }] }
        : {}),
    };
  });
  return [
    { id: guia.id, titulo: guia.titulo, resumo: guia.resumo, passos: [...passos, ...extra] },
    {
      id: `${guia.id}-problemas`,
      titulo: 'Se não funcionar',
      resumo: 'O que você vê, e o que quase sempre é.',
      passos: guia.quandoNaoFunciona.map((s, i) => ({
        id: `${guia.id}-sintoma-${i + 1}`,
        titulo: s.sintoma,
        texto: CORRECOES[s.sintoma]?.causa ?? s.causa,
      })),
    },
  ];
}

export const CONEXOES: CapituloDoGuia = {
  id: 'conexoes',
  titulo: 'Conexões',
  resumo:
    'WhatsApp, link na página e Meta Ads: o passo a passo de cada conexão, as pedras do caminho e o que fazer quando parece certo e nada acontece.',
  icone: Plug,
  secoes: [
    ...secoesDaConexao(GUIA_WHATSAPP, {
      1: img(
        'monitorador-novo',
        'Janela "Adicionar monitorador" com o nome do aparelho preenchido',
        ['Nome do aparelho, não do grupo', 'Criar e conectar (abre o QR code)'],
      ),
      3: img('grupos', 'Tela de Grupos com doze grupos "Ofertas" monitorados', [
        'Escolher grupos',
        'Filtros: todos, recebendo, cheios, pausados',
        'Sincronizar todos (refaz a varredura)',
        'Lotação contra o limite de segurança',
      ]),
    }),
    ...secoesDaConexao(
      GUIA_REDIRECIONADOR,
      {
        0: img('redirecionador-novo', 'Janela "Novo redirecionador"', [
          'Nome do link',
          'Campanha (o bloco de grupos)',
          'Como repartir os cliques entre os grupos',
        ]),
        1: img('redirecionador-instalacao', 'Gaveta do link aberta na aba Instalação', [
          'Abas: Instalação, Cliques, Quem clicou, Próximos grupos, Página',
          'Copiar o trecho pronto',
        ]),
      },
      [
        {
          id: 'redirecionador-parametros',
          titulo: 'E no anúncio: os parâmetros de URL',
          onde: 'Gerenciador de Anúncios › Anúncio › Parâmetros de URL',
          texto:
            'Cole exatamente o texto abaixo no campo "Parâmetros de URL" de cada anúncio. A Meta troca o que está entre chaves pelos números reais da campanha, do conjunto e do anúncio — e o MGDLead usa isso para ligar o clique ao gasto.',
          codigo: PARAMETROS_DE_URL,
          notas: [
            {
              tom: 'importante',
              titulo: 'Sem os parâmetros, o custo por entrada não fecha',
              texto:
                'O clique chega como "sem origem": a pessoa entra no grupo, mas o painel não sabe qual campanha da Meta a trouxe. O Dashboard avisa em "Anúncios sem identificação", com o código pronto para copiar.',
            },
          ],
        },
      ],
    ),
    ...secoesDaConexao(GUIA_META_ADS, {
      4: img('meta-ads-conectar', 'Janela "Conectar conta de anúncios"', [
        'Número da conta (act_…)',
        'Token do Usuário do Sistema (começa com EAA)',
        'Testar conexão antes de salvar',
      ]),
    }),
    {
      id: 'shopee-conexao',
      titulo: 'Conectar a Shopee (afiliados)',
      resumo: 'Opcional: faz a comissão chegar sozinha e mostra o lucro por lead de cada bloco.',
      passos: [
        {
          id: 'shopee-passo',
          titulo: 'AppID e senha da API da Shopee Affiliate',
          onde: 'Aquisição › Shopee',
          texto:
            'Só o Proprietário conecta. Em Shopee › "Conectar a Shopee", informe o AppID e a senha da API do programa de afiliados. A partir daí a comissão é importada sozinha, e cada Sub ID pode ser ligado ao bloco que gerou a venda.',
          imagem: img('shopee', 'Tela da Shopee antes de conectar', ['Conectar a Shopee']),
        },
      ],
    },
  ],
};
